import React, { useEffect, useMemo, useState } from 'react'
import {
  Alert, Box, Chip, CircularProgress, Paper, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, ToggleButton, ToggleButtonGroup, Typography,
} from '@mui/material'
import EventIcon         from '@mui/icons-material/EventOutlined'
import VideocamIcon      from '@mui/icons-material/VideocamOutlined'
import LocationOnIcon    from '@mui/icons-material/LocationOnOutlined'
import { useNavigate } from 'react-router-dom'
import { supabase } from '../../supabaseClient'
import { useAuth } from '../../auth/AuthContext'

interface MeetingRow {
  meeting_id:    string
  class_id:      string
  class_name:    string
  kind:          'group' | 'office_hours'
  scheduled_at:  string
  duration_min:  number
  format:        'in_person' | 'online' | 'simulcast'
  status:        'scheduled' | 'cancelled' | 'held'
  enrolled:      number
}

const FMT_LABEL: Record<string, string> = {
  in_person: 'In-person',
  online:    'Online',
  simulcast: 'Simulcast',
}

function fmtDayHeader(s: string) {
  return new Date(s).toLocaleDateString('en-US', {
    weekday: 'long', month: 'long', day: 'numeric',
  })
}
function fmtTime(s: string) {
  return new Date(s).toLocaleString('en-US', {
    hour: 'numeric', minute: '2-digit', timeZone: 'America/Los_Angeles',
  })
}

/**
 * Schedule page — upcoming meetings for the user.
 *
 * Scope:
 *   admin / office_manager  → all meetings
 *   mentor / instructor     → meetings on classes they mentor
 *   student                 → not routed here
 *
 * Window toggle: Upcoming (next 4 weeks) | Past (last 4 weeks).
 * Grouped by day.
 */
export default function SchedulePage() {
  const { session, role } = useAuth()
  const navigate          = useNavigate()
  const [rows,    setRows]    = useState<MeetingRow[]>([])
  const [window,  setWindow]  = useState<'upcoming' | 'past'>('upcoming')
  const [loading, setLoading] = useState(true)
  const [error,   setError]   = useState<string | null>(null)

  async function load() {
    if (!session) return
    setLoading(true)
    setError(null)
    try {
      // Resolve class scope
      let classIds: string[] = []
      if (role === 'admin' || role === 'office_manager') {
        const { data: classes } = await supabase.from('class').select('class_id')
        classIds = (classes ?? []).map(c => c.class_id)
      } else {
        const { data: cm } = await supabase.from('class_mentor').select('class_id').eq('user_id', session.user.id)
        classIds = (cm ?? []).map(r => r.class_id)
      }
      if (classIds.length === 0) { setRows([]); setLoading(false); return }

      const nowIso = new Date().toISOString()
      // Show ALL upcoming or ALL past meetings on the user's classes — no
      // arbitrary 4-week window. A capped window was hiding meetings that
      // exist further out, surprising mentors who saw "no meetings" when
      // there were several scheduled for the next semester.
      const meetingQuery = supabase
        .from('class_meeting')
        .select('meeting_id, class_id, kind, scheduled_at, duration_min, format, status')
        .in('class_id', classIds)
        .order('scheduled_at', { ascending: window === 'upcoming' })
      const meetingQueryFiltered = window === 'upcoming'
        ? meetingQuery.gte('scheduled_at', nowIso)
        : meetingQuery.lt('scheduled_at', nowIso)

      const [{ data: meetings }, { data: classes }, { data: enrollments }] = await Promise.all([
        meetingQueryFiltered,
        supabase.from('class').select('class_id, name').in('class_id', classIds),
        supabase.from('class_enrollment').select('class_id').in('class_id', classIds).eq('status', 'active'),
      ])

      const classMap   = Object.fromEntries((classes ?? []).map((c: any) => [c.class_id, c.name]))
      const enrollMap: Record<string, number> = {}
      for (const e of enrollments ?? []) enrollMap[e.class_id] = (enrollMap[e.class_id] ?? 0) + 1

      setRows((meetings ?? []).map((m: any) => ({
        meeting_id:    m.meeting_id,
        class_id:      m.class_id,
        class_name:    classMap[m.class_id] ?? '—',
        kind:          m.kind,
        scheduled_at:  m.scheduled_at,
        duration_min:  m.duration_min,
        format:        m.format,
        status:        m.status,
        enrolled:      enrollMap[m.class_id] ?? 0,
      })))
    } catch (e: any) {
      setError(e.message)
    } finally {
      setLoading(false)
    }
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load() }, [session, role, window])

  // Group by day-string (YYYY-MM-DD in PT)
  const groupedByDay = useMemo(() => {
    const groups: Record<string, MeetingRow[]> = {}
    for (const r of rows) {
      const day = r.scheduled_at.slice(0, 10)
      if (!groups[day]) groups[day] = []
      groups[day].push(r)
    }
    return groups
  }, [rows])

  const dayKeys = Object.keys(groupedByDay).sort((a, b) =>
    window === 'upcoming' ? a.localeCompare(b) : b.localeCompare(a)
  )

  if (role !== 'admin' && role !== 'office_manager' && role !== 'instructor' && role !== 'mentor') {
    return <Box sx={{ p: 4 }}><Alert severity="warning">Staff only.</Alert></Box>
  }

  return (
    <Box sx={{ p: { xs: 2, sm: 4 }, maxWidth: 1100 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1.5, mb: 3 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.25 }}>My Schedule</Typography>
          <Typography variant="body2" color="text.secondary">
            {window === 'upcoming'
              ? 'All upcoming meetings on your classes.'
              : 'All past meetings on your classes.'}
          </Typography>
        </Box>
        <ToggleButtonGroup
          exclusive size="small" value={window}
          onChange={(_, v) => v && setWindow(v)}
        >
          <ToggleButton value="upcoming" sx={{ textTransform: 'none' }}>Upcoming</ToggleButton>
          <ToggleButton value="past"     sx={{ textTransform: 'none' }}>Past</ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {loading ? (
        <Box sx={{ py: 6, textAlign: 'center' }}><CircularProgress /></Box>
      ) : rows.length === 0 ? (
        <Paper variant="outlined" sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="text.secondary">
            No {window} meetings.
          </Typography>
        </Paper>
      ) : (
        dayKeys.map(day => (
          <Box key={day} sx={{ mb: 3 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.75, color: 'text.secondary' }}>
              {fmtDayHeader(groupedByDay[day][0].scheduled_at)}
            </Typography>
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600, width: 120 }}>Time</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Class</TableCell>
                    <TableCell sx={{ fontWeight: 600, width: 130 }}>Kind</TableCell>
                    <TableCell sx={{ fontWeight: 600, width: 130 }}>Format</TableCell>
                    <TableCell sx={{ fontWeight: 600, width: 110 }}>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {groupedByDay[day].map(r => (
                    <TableRow
                      key={r.meeting_id}
                      hover
                      sx={{ cursor: 'pointer' }}
                      onClick={() => navigate(`/classes/${r.class_id}`)}
                    >
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>{fmtTime(r.scheduled_at)}</Typography>
                        <Typography variant="caption" color="text.secondary">{r.duration_min} min</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>{r.class_name}</Typography>
                        {r.kind === 'group' && r.enrolled > 0 && (
                          <Typography variant="caption" color="text.secondary">{r.enrolled} student{r.enrolled !== 1 ? 's' : ''}</Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={r.kind === 'office_hours' ? 'Office hour' : 'Group'}
                          color={r.kind === 'office_hours' ? 'secondary' : 'primary'}
                          variant="outlined"
                          icon={<EventIcon fontSize="small" />}
                        />
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          {r.format === 'in_person' && <LocationOnIcon fontSize="small" sx={{ color: 'text.secondary' }} />}
                          {r.format === 'online'    && <VideocamIcon fontSize="small" sx={{ color: 'text.secondary' }} />}
                          {r.format === 'simulcast' && <>
                            <LocationOnIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                            <VideocamIcon   fontSize="small" sx={{ color: 'text.secondary' }} />
                          </>}
                          <Typography variant="body2">{FMT_LABEL[r.format]}</Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        {r.status === 'scheduled' && <Chip size="small" label="Scheduled" />}
                        {r.status === 'held'      && <Chip size="small" color="success" label="Held" />}
                        {r.status === 'cancelled' && <Chip size="small" color="error" label="Cancelled" />}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        ))
      )}
    </Box>
  )
}
